import { NgIf, NgFor } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationItem,
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/shared/services/user.service';
import { AuthUser } from 'app/core/models/auth_user.model';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'environments/environment';
import { SubsidiaryContextService } from 'app/services/subsidiary/subsidiary-context.service';
import { Subsidiary } from 'app/types/subsidiary.type';
import { entradasDelMenuPara } from './entradas-del-menu';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        UserComponent,
        NgIf,
        NgFor,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        MatInputModule,
        ReactiveFormsModule,
        RouterOutlet,
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;

    navigationItems: FuseNavigationItem[] = entradasDelMenuPara(null);

    user: AuthUser;
    subsidiaries: Subsidiary[] = [];
    selectedSubsidiaryId: number | null = null;
    selectedSubsidiaryLabel = '';
    filteredSubsidiaries: Subsidiary[] = [];
    subsidiaryControl = new FormControl<Subsidiary | string>('');
    showSubsidiarySelector = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _subsidiaryContextService: SubsidiaryContextService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    get isProd(): boolean {
        return environment.production;
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        // this._navigationService.navigation$
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe((navigation: Navigation) => {
        //         this.navigation = navigation;
        //     });

        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: AuthUser) => {
                this.user = user;
                this.navigationItems = entradasDelMenuPara(user?.role);
            });

        this._subsidiaryContextService.context$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((context) => {
                this.subsidiaries = context.subsidiaries;
                this.selectedSubsidiaryId = context.selectedSubsidiary?.id ?? null;
                this.selectedSubsidiaryLabel = this.formatSubsidiaryName(context.selectedSubsidiary ?? null);
                this.showSubsidiarySelector = context.canSelect;
                this.filteredSubsidiaries = [...this.subsidiaries];

                const nextValue = context.selectedSubsidiary ?? '';
                this.subsidiaryControl.setValue(nextValue, { emitEvent: false });
            });

        this.initializeSubsidiaryAutocomplete();

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
    clearSubsidiarySelection(): void {
        this.subsidiaryControl.setValue('', { emitEvent: false });
        this.filteredSubsidiaries = [...this.subsidiaries];
        this._subsidiaryContextService.selectSubsidiary(null);
    }

    formatSubsidiaryName(subsidiary: Subsidiary | string | null): string {
        if (!subsidiary) {
            return '';
        }

        if (typeof subsidiary === 'string') {
            return subsidiary;
        }

        return `${subsidiary.code} - ${subsidiary.fantasyName}`;
    }

    onSubsidiarySelected(event: MatAutocompleteSelectedEvent): void {
        const selected = event.option.value as Subsidiary;

        if (selected) {
            this.subsidiaryControl.setValue(selected, { emitEvent: false });
            this.filteredSubsidiaries = [...this.subsidiaries];
            this._subsidiaryContextService.selectSubsidiary(selected.id);
        }
    }

    private initializeSubsidiaryAutocomplete(): void {
        this.subsidiaryControl.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
                if (typeof value === 'string') {
                    this.filteredSubsidiaries = this.filterSubsidiaries(value);
                } else {
                    this.filteredSubsidiaries = [...this.subsidiaries];
                }
            });
    }

    private filterSubsidiaries(term: string): Subsidiary[] {
        const normalized = term.trim().toLowerCase();

        if (!normalized) {
            return [...this.subsidiaries];
        }

        return this.subsidiaries.filter((subsidiary) =>
            this.formatSubsidiaryName(subsidiary).toLowerCase().includes(normalized),
        );
    }
}
